import { Banknote, CreditCard, DoorClosed, DoorOpen, QrCode } from 'lucide-react';

// Helpers de dinheiro em formato BR ("1.234,56") — mesma técnica usada em
// `pages/services/List.js` (`parseBRMoney`/`formatBRMoney`) e nos protótipos
// `prototipo-lista-caixas.html`/`prototipo-painel-caixa.html`
// (`parseBR`/`formatBR`).
export function parseBRMoney(value) {
    if (value === undefined || value === null || value === '') return 0;
    const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
}

export function formatBRMoney(value) {
    return (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Converte "dd/mm/aaaa hh:mm:ss" (formato de `CashDetails.OpenDate`/
// `DateOperationFormatted`, vindo pronto do backend) para "aaaa-mm-dd", que é
// o que `POST /cashregister/{id}/close` espera em `start_date`/`end_date`
// (comparado como `::date` no Postgres). Usado para que o fechamento
// considere as vendas desde a abertura real da sessão, não só "hoje" — sem
// isso, uma sessão aberta em um dia anterior faria o backend comparar o
// valor contado contra "vendas de hoje" (quase sempre zero) e rejeitar o
// fechamento com 400 mesmo no caminho feliz.
export function parseBRDateToISO(value) {
    if (!value || typeof value !== 'string') return null;
    const [datePart] = value.split(' ');
    const [d, m, y] = (datePart || '').split('/');
    if (!d || !m || !y) return null;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

// Domínio "Forma de pagamento" (§4) reaproveitado pela tabela de conciliação
// de fechamento (`recon-table`) e pelas linhas `paid-row` do Painel do caixa.
//
// Lacuna de backend documentada (aproximação deliberada, sem inventar dado):
// os protótipos (`prototipo-painel-caixa.html`/`prototipo-lista-caixas.html`)
// mostram 4 formas (Dinheiro/PIX/Débito/Crédito), mas nenhum endpoint de
// `cashregister` (`/resume`, `/view`, `/close`) separa cartão de
// débito/crédito — só existe um total agregado de "maquininha"
// (`GetTotalGrossAmountMachineByPeriod`/`GetTotalNetAmountMachineByPeriod`).
// O próprio fechamento (`cashregister.Closer.Close`) também não persiste os
// 4 valores informados em lugar nenhum — usa a soma deles só para validar
// que o total contado não excede o total vendido, então uma divisão
// débito/crédito arbitrária não teria efeito real além da soma. Por isso
// esta tela usa 3 formas agregadas (Dinheiro/PIX/Cartão) em vez das 4 do
// protótipo — inventar uma proporção débito×crédito para preencher uma 4ª
// linha seria dado fabricado, não uma leitura real da API.
export const PAYMENT_METHODS = [
    { key: 'dinheiro', label: 'Dinheiro', icon: Banknote, tone: 'success' },
    { key: 'pix', label: 'PIX', icon: QrCode, tone: 'brand2' },
    { key: 'cartao', label: 'Cartão (débito + crédito)', icon: CreditCard, tone: 'purple' },
];

export function paymentToneColors(theme, tone) {
    switch (tone) {
        case 'success':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'info':
            return { bg: theme.palette.info.bg, fg: theme.palette.primary.main };
        case 'purple':
            return { bg: theme.palette.secondary.bg, fg: theme.palette.secondary.main };
        case 'brand2':
            return { bg: theme.palette.info.bg, fg: theme.palette.brand2.main };
        default:
            return { bg: theme.palette.background.subtle, fg: theme.palette.text.disabled };
    }
}

// Domínio novo "Evento de caixa" (§4, extensão adicionada por esta tarefa —
// ver DESIGN-SYSTEM.md) — usado pela timeline do Histórico do caixa.
export const CASH_EVENT_META = {
    open: { label: 'Abertura', tone: 'success', icon: DoorOpen, amountLabel: 'Valor inicial' },
    cash_bleeding: { label: 'Sangria', tone: 'danger', icon: Banknote, amountLabel: 'Valor da sangria' },
    closed: { label: 'Fechamento', tone: 'neutral', icon: DoorClosed, amountLabel: 'Valor apurado' },
};

export function eventToneColors(theme, tone) {
    switch (tone) {
        case 'success':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'danger':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'neutral':
        default:
            return { bg: theme.palette.background.subtle, fg: theme.palette.text.disabled };
    }
}

// Casca visual do campo "ff-input"/"ff-textarea" (52px/auto, radius 12px,
// fundo surface-2, label flutuante interno) — cópia 1:1 de
// `standaloneFieldSx`/`standaloneInputBoxSx` já usados em
// `pages/categories/Form.js`/`pages/services/Form.js`/`pages/products/Form.js`.
export function useStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

// setting-row (§5.6, mesmo padrão de `prototipo-lista-caixas.html`/
// `prototipo-configuracoes-v2.html`) — ícone + título/descrição + switch.
export function settingRowSx(isLast) {
    return {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        py: '14px',
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
    };
}

// Resumo da sessão atual do caixa a partir de `GET /cashregister/{id}/resume`
// e `GET /cashregister/{id}/history` (mesmas derivações do Painel do caixa —
// ver `View.js`): janela da sessão, sangrias dela e totais por forma de
// pagamento. "Vendas em dinheiro" não é um campo direto da API: é o bruto
// menos PIX e menos maquininha.
export function summarizeCashSession(resume, history) {
    const list = Array.isArray(history) ? history : [];
    const isOpen = resume?.details?.status === 'open';

    let sessionWindow = { start: null, end: null };
    if (list.length) {
        if (isOpen) {
            const openEvt = list.find((h) => h.TypeOperation === 'open');
            sessionWindow = { start: openEvt ? new Date(openEvt.DateOperation) : null, end: new Date() };
        } else {
            const closedEvt = list.find((h) => h.TypeOperation === 'closed');
            if (closedEvt) {
                const closedIdx = list.indexOf(closedEvt);
                const openEvt = list.slice(closedIdx + 1).find((h) => h.TypeOperation === 'open');
                sessionWindow = { start: openEvt ? new Date(openEvt.DateOperation) : null, end: new Date(closedEvt.DateOperation) };
            }
        }
    }

    const sangriasList = list.filter((h) => {
        if (h.TypeOperation !== 'cash_bleeding') return false;
        if (!sessionWindow.start) return true;
        const d = new Date(h.DateOperation);
        return d >= sessionWindow.start && d <= (sessionWindow.end || new Date());
    });
    const totalSangrias = sangriasList.reduce((sum, s) => sum + parseBRMoney(s.Amount), 0);

    const bruto = parseBRMoney(resume?.total_gross_amount);
    const liquido = parseBRMoney(resume?.total_net_amount);
    const pix = parseBRMoney(resume?.total_pix);
    const machineGross = parseBRMoney(resume?.total_gross_machine);
    const vendasDinheiro = Math.max(bruto - pix - machineGross, 0);
    const valorInicial = parseBRMoney(resume?.details?.initial_amount);
    const valorEsperado = parseBRMoney(resume?.available_cash);

    return { isOpen, sessionWindow, sangriasList, totalSangrias, bruto, liquido, pix, machineGross, vendasDinheiro, valorInicial, valorEsperado };
}
